/// <reference path="../../../scripts/typings/Nema.Crew.Model.d.ts" />

import service = require("./breeze.entitymanager.service");

/**
 * Provides a base implementation of a generic Data Service using BreezeJS.
 */
export abstract class BaseDataService<TEntity> implements IDataService<TEntity> {

    protected entityManager: breeze.EntityManager;
    protected breeze;
    protected $log: ng.ILogService;
    protected $q: ng.IQService;
    protected entityCollection: breeze.Entity[];

    constructor(entityManagerService: service.EntityManagerService, breeze, $log, $q: ng.IQService) {
        this.entityManager = entityManagerService.getEntityManager(() => this.rejectChanges());
        this.breeze = breeze;
        this.$log = $log;
        this.$q = $q;
        this.entityCollection = [];
    }

    /**
     * Gets the name of the entity in singular to which this dataservice operates on.
     */
    protected abstract get EntityName(): string;

    /**
     * Gets the name of the entity in plural to which this dataservice operates on.
     */
    protected abstract get EntitiesName(): string;

    /**
     * Gets all the entities.
     */
    public getAll(_active: boolean = true): ng.IPromise<TEntity[]> {

        var self = this;

        var entityQuery = self.createEntityQuery()
            //.where("Active", "==", active)
            .orderBy('Name')
            .using(this.entityManager);

        var promise = entityQuery.execute()
            // ignore remote query results and chain to local query. Discarded the query results ... but items remain in cache
            .then(() => {
                self.entityCollection = entityQuery.using(self.breeze.FetchStrategy.FromLocalCache).executeLocally();
                return <TEntity[]><any>self.entityCollection;
            });

        return promise;
    }

    /**
     * Gets an entity by Id.
     */
    public get(id: any): ng.IPromise<TEntity> {
        return this.fetchEntityByKey(this.EntityName, id);
    }

    protected fetchEntityByKey<T>(entityName: string, id: any): ng.IPromise<T> {
        return <ng.IPromise<T>><any>this.entityManager
            .fetchEntityByKey(entityName, id, true)
            .then(entityByKeyResult => entityByKeyResult.entity);
    }

    /**
     * Creates the specified entity.
     */
    public create(entity: TEntity): ng.IPromise<breeze.SaveResult> {
        var self = this;

        // First create the Parent Entity
        var newEntity = self.entityManager.createEntity(this.EntityName, entity);
        self.entityCollection.push(newEntity);

        var saveParent = self.entityManager
            .saveChanges([newEntity]);

        // Then create any possible navigational/related Entities
        var saveRelations = saveParent.then(() => {           
            return self.entityManager.saveChanges();
        });

        return <ng.IPromise<breeze.SaveResult>><any>Promise.all([saveParent, saveRelations]).then(res => res[0]);
    }
    
    /**
     * Deletes the specified entity.
     */
    public delete(entity: TEntity): Promise<any> {
        const breezeEntity = <breeze.Entity><any>entity;

        // First delete the Steps
        const deleteRelations = this.deleteRelatedEntities(entity);

        // Then, delete the WorkSchedule
        var deleteParent = deleteRelations
            .then(_ => {
                breezeEntity.entityAspect.entityState = this.breeze.EntityState.Deleted;
                const index = this.entityCollection.indexOf(breezeEntity);
                this.entityCollection.splice(index, 1);

                return this.entityManager.saveChanges();
            });

        return Promise.all([deleteRelations, deleteParent]);
    }
    
    /**
     * Saves the specified entity.
     */
    public save(_entity) {
        return this.entityManager.saveChanges();
    }

    /**
     * Has the data context changed.
     */
    public hasChanges(): boolean {
        return this.entityManager.hasChanges();
    }

    /**
     * Discard all changes.
     */
    public rejectChanges(): void {
        this.entityManager.rejectChanges();
    }

    public hasChanged(hasChanged: boolean = true): void {
        (<any>this.entityManager)._hasChanges = hasChanged;
    }

    public removeMapping<TRelatedEntity>(mapping: TRelatedEntity, removeFromList: () => void): void {
        var entity = (<breeze.Entity><any>mapping);
        var currentState = entity.entityAspect.entityState;

        if (currentState === this.breeze.EntityState.Added) {
            entity.entityAspect.rejectChanges();
        } else {
            entity.entityAspect.entityState = this.breeze.EntityState.Deleted;
            removeFromList();
            this.hasChanged();
        }
    }

    /**
     * Option to delete (any possible) relations to the specified entity.
     */
    protected deleteRelatedEntities(_entity: TEntity): Promise<void> {
        // By default - there is nothing to be done
        return new Promise(function (resolve) {
            resolve();
        });
    }

    protected createEntityQuery(): breeze.EntityQuery {
        return new this.breeze.EntityQuery(this.EntitiesName);
    }
}

/**
 * Defines a generic interface for handling entities.
 */
export interface IDataService<TEntity> {

    /**
     * Gets all the entities.
     */
    getAll(active?: boolean): ng.IPromise<TEntity[]>;

    /**
     * Gets an entity by Id.
     */
    get(id: any): ng.IPromise<TEntity>;

    /**
     * Saves the specified entity.
     */
    save(entity);

    /**
     * Deletes the specified entity.
     */
    delete(entity): Promise<any>;

    /**
     * Creates the specified entity.
     */
    create(entity: TEntity): ng.IPromise<breeze.SaveResult>;

    /**
     * Has the data context changed.
     */
    hasChanges(): boolean;

    /**
     * Discard all changes.
     */
    rejectChanges(): void;

    /**
     * Override indication that changes have occurred.
     */
    hasChanged(hasChanged?: boolean): void;
    
    removeMapping<TRelatedEntity>(mapping: TRelatedEntity, removeFromList?: () => void): void;
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/@angularclass/hmr-loader!./node_modules/tslint-loader!./client/app/core/base.dataservice.ts